import { useState, useEffect } from 'react';
import { clients, calls, users as usersApi } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Phone, PhoneOutgoing, PhoneIncoming, Building, Edit, User } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

export default function Llamadas() {
  const { user } = useAuth();
  const [callsList, setCallsList] = useState([]);
  const [clientsList, setClientsList] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [clientFilter, setClientFilter] = useState('all');

  const [formData, setFormData] = useState({
    client_id: '', contact_name: '', subject: '', call_datetime: '', caller_user_id: '',
    duration_minutes: '', description: '', call_type: 'outbound',
  });
  const [editData, setEditData] = useState({});

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [cr, clr, ur] = await Promise.all([clients.getAll(), calls.getAll(), usersApi.getAll()]);
      setClientsList(cr.data);
      setCallsList(clr.data);
      setUsersList(ur.data);
    } catch (err) { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.client_id || !formData.contact_name || !formData.subject || !formData.call_datetime) {
      toast.error('Empresa, persona contactada, tema y fecha/hora son requeridos');
      return;
    }
    try {
      await calls.create({
        ...formData,
        duration_minutes: parseInt(formData.duration_minutes) || 0,
        caller_user_id: formData.caller_user_id || undefined,
      });
      toast.success('Llamada registrada');
      setDialogOpen(false);
      setFormData({ client_id: '', contact_name: '', subject: '', call_datetime: '', caller_user_id: '', duration_minutes: '', description: '', call_type: 'outbound' });
      loadData();
    } catch (err) { toast.error('Error al registrar llamada'); }
  };

  const handleOpenEdit = (call) => {
    setSelectedCall(call);
    setEditData({
      contact_name: call.contact_name || '',
      subject: call.subject || '',
      duration_minutes: call.duration_minutes || 0,
      description: call.description || '',
      call_type: call.call_type || 'outbound',
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!selectedCall) return;
    try {
      await calls.update(selectedCall.call_id, editData);
      toast.success('Llamada actualizada');
      setEditDialogOpen(false);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al actualizar'); }
  };

  const filtered = callsList.filter(c => {
    if (clientFilter !== 'all' && c.client_id !== clientFilter) return false;
    return true;
  });

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="llamadas-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight flex items-center gap-3">
            <Phone className="h-8 w-8 text-primary" />
            Llamadas Realizadas
          </h1>
          <p className="text-muted-foreground">Registro de llamadas con empresas y contactos</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="add-llamada-btn">
          <Plus className="h-4 w-4 mr-2" />Registrar Llamada
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Total Llamadas</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold">{callsList.length}</p></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Salientes</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold text-blue-500">{callsList.filter(c => c.call_type === 'outbound').length}</p></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Entrantes</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold text-emerald-500">{callsList.filter(c => c.call_type === 'inbound').length}</p></CardContent></Card>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3">
        <Building className="h-4 w-4 text-muted-foreground" />
        <Select value={clientFilter} onValueChange={setClientFilter}>
          <SelectTrigger className="w-[200px] h-8"><SelectValue placeholder="Empresa" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las empresas</SelectItem>
            {clientsList.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.company || c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">No hay llamadas registradas</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="uppercase text-xs">Tipo</TableHead>
                  <TableHead className="uppercase text-xs">Empresa</TableHead>
                  <TableHead className="uppercase text-xs">Persona Contactada</TableHead>
                  <TableHead className="uppercase text-xs">Tema</TableHead>
                  <TableHead className="uppercase text-xs">Realizada por</TableHead>
                  <TableHead className="uppercase text-xs">Duracion</TableHead>
                  <TableHead className="uppercase text-xs">Fecha</TableHead>
                  <TableHead className="uppercase text-xs">Registrada por</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(c => (
                  <TableRow key={c.call_id}>
                    <TableCell>
                      {c.call_type === 'outbound'
                        ? <Badge variant="outline" className="gap-1"><PhoneOutgoing className="h-3 w-3 text-blue-500" />Saliente</Badge>
                        : <Badge variant="outline" className="gap-1"><PhoneIncoming className="h-3 w-3 text-emerald-500" />Entrante</Badge>}
                    </TableCell>
                    <TableCell><div className="flex items-center gap-1"><Building className="h-3 w-3 text-muted-foreground" />{c.client_name}</div></TableCell>
                    <TableCell className="font-medium">{c.contact_name || '-'}</TableCell>
                    <TableCell>
                      <div className="font-medium">{c.subject || '-'}</div>
                      {c.description && <div className="text-xs text-muted-foreground line-clamp-1">{c.description}</div>}
                    </TableCell>
                    <TableCell className="text-sm">{c.caller_name || c.user_name || '-'}</TableCell>
                    <TableCell className="text-sm">{c.duration_minutes > 0 ? `${c.duration_minutes} min` : '-'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{c.call_datetime ? formatDate(c.call_datetime) : formatDate(c.created_at)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{c.created_by_name || c.user_name}</TableCell>
                    <TableCell>
                      {(c.created_by === user?.user_id || c.user_id === user?.user_id || user?.role === 'admin') && (
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => handleOpenEdit(c)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-xl flex items-center gap-2"><Phone className="h-5 w-5" />Registrar Llamada</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Empresa *</Label>
                <Select value={formData.client_id} onValueChange={(v) => setFormData({ ...formData, client_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>{clientsList.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.company || c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Persona contactada *</Label>
                <Input placeholder="Nombre del contacto" value={formData.contact_name} onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })} required />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Tema de la llamada *</Label>
              <Input placeholder="Ej: Seguimiento de cotizacion" value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} required />
            </div>
            <div className="space-y-1.5">
              <Label>Fecha y hora de la llamada *</Label>
              <Input type="datetime-local" value={formData.call_datetime} onChange={(e) => setFormData({ ...formData, call_datetime: e.target.value })} required />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Quien llamo</Label>
                <Select value={formData.caller_user_id} onValueChange={(v) => setFormData({ ...formData, caller_user_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Yo" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self">Yo</SelectItem>
                    {usersList.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Tipo</Label>
                <Select value={formData.call_type} onValueChange={(v) => setFormData({ ...formData, call_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="outbound">Saliente</SelectItem>
                    <SelectItem value="inbound">Entrante</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Duracion (min)</Label>
                <Input type="number" min="0" value={formData.duration_minutes} onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Descripcion (opcional)</Label>
              <Textarea placeholder="Detalles de la llamada..." value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} />
            </div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button><Button type="submit">Registrar Llamada</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl flex items-center gap-2"><Edit className="h-5 w-5" />Editar Llamada</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Persona contactada</Label><Input value={editData.contact_name || ''} onChange={(e) => setEditData({ ...editData, contact_name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Tema</Label><Input value={editData.subject || ''} onChange={(e) => setEditData({ ...editData, subject: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Tipo</Label>
                <Select value={editData.call_type || 'outbound'} onValueChange={(v) => setEditData({ ...editData, call_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="outbound">Saliente</SelectItem><SelectItem value="inbound">Entrante</SelectItem></SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Duracion (min)</Label><Input type="number" value={editData.duration_minutes || ''} onChange={(e) => setEditData({ ...editData, duration_minutes: parseInt(e.target.value) || 0 })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Descripcion</Label><Textarea value={editData.description || ''} onChange={(e) => setEditData({ ...editData, description: e.target.value })} rows={2} /></div>
            <DialogFooter><Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancelar</Button><Button onClick={handleUpdate}>Guardar</Button></DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
